import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, EnvelopeSimple, Warning, Eye, PaperPlaneRight, ArrowsClockwise } from "@phosphor-icons/react";

// Preview + manual-send a PM-branded owner authorization email for a job.
// Nothing fires automatically — admin reviews subject/recipients/HTML, then hits Send.
export default function OwnerNoticeModal({ jobId, onClose, onSent }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [subject, setSubject] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [costCap, setCostCap] = useState(500);
  const [dispatchWindow, setDispatchWindow] = useState("within 1.5 hours");

  const loadPreview = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.post(`/jobs/${jobId}/owner-notice/preview`, {
        subject: subject || undefined,
        to_email: toEmail || undefined,
        cost_cap: Number(costCap) || undefined,
        dispatch_window: dispatchWindow || undefined,
      });
      setPreview(data);
      if (!subject) setSubject(data.subject);
      if (!toEmail) setToEmail(data.to_email || "");
      if (data.cost_cap != null) setCostCap(data.cost_cap);
      if (data.dispatch_window) setDispatchWindow(data.dispatch_window);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not build preview.");
    } finally { setLoading(false); }
  };
  useEffect(() => { loadPreview(); /* eslint-disable-next-line */ }, []);

  const send = async () => {
    if (!toEmail.trim()) return toast.error("Recipient email required.");
    if (!subject.trim()) return toast.error("Subject required.");
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/owner-notice/send`, {
        subject, to_email: toEmail, cost_cap: Number(costCap), dispatch_window: dispatchWindow,
      });
      if (data.sent) {
        toast.success(`Sent to ${data.to} · from "${data.from_name}"`);
        onSent?.();
        onClose();
      } else {
        toast.error("Email service did not confirm delivery. Check backend logs.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4" data-testid="owner-notice-modal">
      <div className="bg-white max-w-4xl w-full rounded-2xl border-2 border-[#0f172a] shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-[#0f172a] flex-shrink-0">
          <div className="flex items-center gap-2">
            <EnvelopeSimple weight="fill" size={22}/>
            <h2 className="font-display font-black text-xl uppercase">Owner Authorization Notice</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="owner-notice-close"><X size={20}/></button>
        </div>

        {loading ? (
          <div className="p-10 text-center font-black uppercase text-sm text-slate-500 tracking-widest">Loading preview…</div>
        ) : error ? (
          <div className="p-6">
            <div className="bg-red-50 border-2 border-red-500 p-4 flex items-start gap-3" data-testid="owner-notice-error">
              <Warning weight="fill" size={22} className="text-red-600 flex-shrink-0 mt-0.5"/>
              <div>
                <div className="font-black uppercase text-sm tracking-widest text-red-800">Can't build preview</div>
                <p className="mt-1 text-xs text-red-900 font-medium">{error}</p>
                <p className="mt-2 text-[11px] text-red-700 font-medium">
                  Fix: Go to Admin → Landlords → Property Manager and fill in company name, contact name, reply-to email + phone.
                  Then assign a landlord to this property from the Property Detail page.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 flex-1 overflow-hidden">
            {/* Left: controls */}
            <div className="p-6 overflow-y-auto border-r-2 border-slate-200 space-y-4">
              {preview?.landlord_missing && (
                <div className="bg-amber-50 border-2 border-amber-500 p-3" data-testid="owner-notice-no-landlord">
                  <div className="font-black uppercase text-[11px] tracking-widest text-amber-800">No landlord assigned</div>
                  <p className="mt-1 text-[11px] text-amber-900 font-medium">Type a recipient below or assign a landlord to this property first.</p>
                </div>
              )}

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">To (landlord email)</label>
                <input type="email" value={toEmail} onChange={e => setToEmail(e.target.value)}
                  data-testid="owner-notice-to-input"
                  className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
                {preview?.landlord_name && (
                  <div className="mt-1 text-[10px] font-black uppercase tracking-widest text-slate-500">Landlord: {preview.landlord_name}</div>
                )}
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Subject</label>
                <input value={subject} onChange={e => setSubject(e.target.value)}
                  data-testid="owner-notice-subject-input"
                  className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Auto-approval cap ($)</label>
                  <input type="number" min="0" value={costCap} onChange={e => setCostCap(e.target.value)}
                    data-testid="owner-notice-cap-input"
                    className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Dispatch window</label>
                  <input value={dispatchWindow} onChange={e => setDispatchWindow(e.target.value)}
                    data-testid="owner-notice-window-input"
                    className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
                </div>
              </div>
              {preview?.work_items?.length > 0 && (
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Grouped work items ({preview.work_items.length})</div>
                  <ul className="mt-1 border-2 border-slate-200 divide-y divide-slate-200">
                    {preview.work_items.map((w, i) => (
                      <li key={i} className="p-2">
                        <div className="text-xs font-bold text-[#0f172a]">{w.title || "(no title)"}</div>
                        {w.description && <div className="text-[11px] text-slate-500 line-clamp-2 mt-0.5">{w.description}</div>}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-1 text-[10px] text-slate-500">Open jobs on this unit are auto-grouped into one notice.</div>
                </div>
              )}

              <button onClick={loadPreview} disabled={loading || busy}
                data-testid="owner-notice-refresh-btn"
                className="w-full h-10 border-2 border-[#0f172a] font-black uppercase tracking-widest text-[11px] hover:bg-slate-50 inline-flex items-center justify-center gap-1.5">
                <ArrowsClockwise size={12}/> Refresh preview
              </button>
            </div>

            {/* Right: preview */}
            <div className="p-6 bg-slate-100 overflow-y-auto">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
                <Eye size={12}/> What the landlord will receive
              </div>
              <div className="bg-white border-2 border-slate-300 p-5" data-testid="owner-notice-preview-html">
                <div className="text-[11px] text-slate-500 border-b border-slate-200 pb-2 mb-3">
                  <div><span className="font-bold text-slate-700">To:</span> {toEmail || "—"}</div>
                  <div><span className="font-bold text-slate-700">Subject:</span> {subject || "—"}</div>
                </div>
                <div dangerouslySetInnerHTML={{ __html: preview?.html || "" }}/>
              </div>
            </div>
          </div>
        )}

        <div className="flex-shrink-0 border-t-2 border-slate-200 p-4 flex gap-2 bg-white">
          <button onClick={onClose} disabled={busy}
            className="flex-1 sm:flex-none h-11 px-6 border-2 border-[#0f172a] font-black uppercase tracking-widest text-xs">
            Cancel
          </button>
          <button onClick={send} disabled={busy || loading || !!error}
            data-testid="owner-notice-send-btn"
            className="flex-1 h-11 px-6 bg-[#00C4CC] text-white font-black uppercase tracking-widest text-xs disabled:opacity-50 inline-flex items-center justify-center gap-2 hover:bg-[#00a8af]">
            <PaperPlaneRight weight="fill" size={14}/> {busy ? "Sending…" : "Send Email Now"}
          </button>
        </div>
      </div>
    </div>
  );
}
